/**
 * Files the user hands the assistant to build with: a kit's GLBs, a video, a
 * sound. They land exactly as a canvas drop lands them (drop-import.ts), so
 * they are ordinary scene files that save and export with the scene, and the
 * model is told what arrived. Images and documents are not scene files here:
 * they stay attachments the model reads.
 */

import { DROPPABLE_ASSET_EXTENSIONS, IMAGE_EXTENSIONS, toDotExtensions } from 'trace-viewer/scene';
import { ActiveStateCommand, boundsInParent, getUndoManager } from 'trace-viewer/editor';
import type { ImportedFile } from '../drop-import';
import { partitionSiblingFiles } from '../sibling-files';

/** Imports files the way a canvas drop does; the editor registers it. */
export type SceneFileImporter = (files: File[]) => Promise<ImportedFile[]>;

/**
 * What a chat attachment adds to the scene instead of showing the model: what
 * a canvas drop takes (models, splats, video, audio), except images.
 */
const SCENE_FILE_EXTENSIONS = new Set([...DROPPABLE_ASSET_EXTENSIONS].filter(ext => !IMAGE_EXTENSIONS.has(ext)));

/** The file picker's `accept` entries for them. */
export const SCENE_FILE_ACCEPT = toDotExtensions(SCENE_FILE_EXTENSIONS).join(',');

export function isSceneFile(fileName: string): boolean {
    return SCENE_FILE_EXTENSIONS.has(fileName.toLowerCase().split('.').pop() ?? '');
}

/**
 * Split a chat attachment batch: scene files, with the files a `.gltf` or
 * `.glxf` among them references (its `.bin`, textures), go into the scene
 * together, as a canvas drop takes them; the rest is for the model to read.
 */
export async function splitSceneFiles(files: File[]): Promise<{ scene: File[]; read: File[] }> {
    const { loadables } = await partitionSiblingFiles(files);
    const companion = (file: File) => !loadables.includes(file);
    return {
        scene: files.filter(file => isSceneFile(file.name) || companion(file)),
        read: files.filter(file => !isSceneFile(file.name) && !companion(file)),
    };
}

const vec = (v: readonly number[]): string => `[${v.map(n => Math.round(n * 1000) / 1000).join(', ')}]`;

/**
 * Hide each imported object as a parts-library source (one undo step each),
 * and describe what landed, for the model's next turn. A build's clone of a
 * hidden source is visible, so nothing sits at the origin until it is placed.
 */
export function hideAndDescribe(imported: ImportedFile[]): string {
    const lines = imported.map(({ name, objects, error }) => {
        if (error || objects.length === 0) return `- ${name}: not imported (${error ?? 'nothing to place'}).`;
        return objects.map(object => {
            const local = boundsInParent(object);
            const { min, max } = object.transform.getHierarchyBoundingVectors(true);
            if (object.active) {
                object.setActive(false);
                getUndoManager().push(new ActiveStateCommand(object, true, false));
            }
            const parent = object.getParent();
            const where = [
                local ? `size ${vec(max.subtract(min).asArray())} and centre ${vec(min.add(max).scale(0.5).asArray())} in the world` : 'draws nothing',
                local ? `bounds min ${vec(local.min)} max ${vec(local.max)} in the frame of ${parent ? `"${parent.name}"` : 'the scene'}` : '',
            ].filter(Boolean).join('; ');
            return `- "${object.name}" from ${name}: ${where}.`;
        }).join('\n');
    });
    return `The user added ${imported.length === 1 ? 'a file' : `${imported.length} files`} to the scene, as a drop on the canvas does: `
        + 'each is a scene file now, saved and exported with the scene. Each object below starts hidden, as a parts library: '
        + 'in build_spatial, clone("<name>") or instances({clone: "<name>"}, …) places visible copies and leaves the source hidden; '
        + 'getObject("<name>") reads the same bounds.\n'
        + lines.join('\n');
}
